import React, { ReactNode } from 'react';
import { Currency } from '@common/components';
import { WorksRateCardItemType } from '@pay-bill/graphql/queries';

export type Filters = {
  name?: string[];
  payCode?: string[];
};

export type ColumnData = WorksRateCardItemType & {
  children?: {
    payCode: string;
  }[];
};

const strFormatted = (str: string) => str.trim().toLowerCase();

export function applyFilterIfKeyIsValid(
  dataSource: ColumnData[],
  key: string[] | undefined,
  cb: (dataSource: ColumnData[], filterCriteria: string[]) => ColumnData[]
) {
  return key ? cb(dataSource, key) : dataSource;
}

export function formatFactor(factor: string, expression: string): ReactNode {
  switch (expression) {
    case 'multiplication':
      return <div>{`${factor}x`}</div>;
    case 'constant':
    case 'addition':
      return <Currency value={Number(factor) / 100} />;
    default:
      return null;
  }
}

export function calculateBillingRate(
  expression: string,
  factor: string,
  billingRegularRate: number
): ReactNode {
  const numberFactor = Number(factor);
  return (
    {
      multiplication: <Currency value={numberFactor * billingRegularRate} />,
      addition: <Currency value={numberFactor / 100 + billingRegularRate} />,
      constant: <Currency value={numberFactor / 100} />,
    }[expression] || null
  );
}

export function filterByPayCodes(
  dataSource: ColumnData[],
  payCodes: string[]
): ColumnData[] {
  return dataSource.filter((item) => {
    if (!Array.isArray(item.children)) {
      return false;
    }

    return item.children.some(
      (child) =>
        child.payCode &&
        payCodes.some(
          (payCode) => strFormatted(payCode) === strFormatted(child.payCode)
        )
    );
  });
}

export function filterByName(
  data: ColumnData[],
  names: string[]
): ColumnData[] {
  const searchString = names[0].trim().toLowerCase();
  return data.filter((obj) =>
    obj.name?.trim().toLowerCase().includes(searchString)
  );
}

export function parsePayCode(payCode: string) {
  try {
    return JSON.parse(payCode).map((code: string) => ({ payCode: code })) || [];
  } catch (error) {
    console.error('BillingRatesDrawer: Error parsing pay code', error);
    return [];
  }
}
